import React, { type InputHTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helpText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helpText, leftIcon, rightIcon, className, ...props }, ref) => {
    const inputClasses = clsx(
      'block w-full rounded-lg border px-3 py-2 text-sm transition-colors',
      'text-foreground bg-card',
      'placeholder:text-muted-foreground',
      'focus:border-primary focus:outline-none focus:ring-1 focus:ring-ring',
      {
        'border-destructive focus:border-destructive focus:ring-destructive': error,
        'border-input': !error,
        'pl-10': leftIcon,
        'pr-10': rightIcon,
      },
      className
    );

    return (
      <div className="space-y-1">
        {label && (
          <label className="block text-sm font-medium text-foreground">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              {leftIcon}
            </div>
          )}
          <input ref={ref} className={inputClasses} {...props} />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              {rightIcon}
            </div>
          )}
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        {helpText && !error && <p className="text-sm text-muted-foreground">{helpText}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;